import { RecommendationService } from "@/services/RecommendationService";
import {
  useAllowAdvizerRecs,
  useUserRecommendedAdvizers,
  useUserHasRecommendedAdvizers,
  useIsRequestingRecommendedAdvizers,
} from "@/store/selectors/userProfileSelectors";
import { Button, Group } from "@mantine/core";
import { Stack } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { useMemo, useCallback } from "react";
import { UserProfileNoResults } from "./UserProfileNoResults";
import { useActiveUser } from "@/store/selectors";
import { UserProfileAdvizerVideoCard } from "./UserProfileVideosWatched";
import { Analytics } from "@/services/Analytics";

export function UserProfileRecommendedAdvizers(props: { onClick: () => void }) {
  const { onClick } = props;
  const { allowAdvizerRecs, levelConfigRequired } = useAllowAdvizerRecs();
  const recommendedAdvizers = useUserRecommendedAdvizers();
  const userHasRecommendedAdvizers = useUserHasRecommendedAdvizers();
  const activeUser = useActiveUser();
  const isRequestingRecommendedAdvizers = useIsRequestingRecommendedAdvizers();

  const buttonText = useMemo(() => {
    if (isRequestingRecommendedAdvizers) {
      return "Getting new Advizer recommendations... (this may take a minute)";
    }
    return userHasRecommendedAdvizers
      ? "Get new Advizer recommendations"
      : "Get Advizer recommendations";
  }, [isRequestingRecommendedAdvizers, userHasRecommendedAdvizers]);

  const handleRefreshClick = useCallback(
    (event: any) => {
      Analytics.click({
        context: "User profile",
        action: "Get recommended advizers button",
        label: buttonText,
        event,
      });

      async function refreshRecommendedAdvizers() {
        try {
          await RecommendationService.getRecommendedAdvizers({
            userId: activeUser?.id || "",
          });
        } catch (error) {
          console.error("Error refreshing recommended advizers", error);
        }
      }
      refreshRecommendedAdvizers();
      onClick();
    },
    [buttonText, activeUser?.id, onClick],
  );

  const MainContent = useMemo(() => {
    if (isRequestingRecommendedAdvizers) {
      return null;
    }

    if (!allowAdvizerRecs) {
      return (
        <UserProfileNoResults
          title={`Complete level ${levelConfigRequired?.level} (${levelConfigRequired?.title.toLowerCase()}) to get recommended advizers.`}
          // buttonText={`Complete level ${levelConfigRequired?.level}`}
          // href={levelConfigRequired?.href}
        />
      );
    }

    if (!userHasRecommendedAdvizers) {
      return null;
    }

    return (
      <Stack maw={700} mt="md">
        {Object.keys(recommendedAdvizers || {}).map((advizerId) => (
          <UserProfileAdvizerVideoCard key={advizerId} advizerId={advizerId} />
        ))}
      </Stack>
    );
  }, [
    isRequestingRecommendedAdvizers,
    allowAdvizerRecs,
    levelConfigRequired,
    userHasRecommendedAdvizers,
    recommendedAdvizers,
  ]);

  return (
    <Stack>
      {MainContent}

      {allowAdvizerRecs && (
        <Group mt="md">
          <Button
            radius="xl"
            size="sm"
            variant="light"
            color="slate"
            onClick={handleRefreshClick}
            leftSection={
              <IconRefresh
                size={16}
                className={isRequestingRecommendedAdvizers ? "spin" : undefined}
              />
            }
            disabled={isRequestingRecommendedAdvizers}
          >
            {buttonText}
          </Button>
        </Group>
      )}
    </Stack>
  );
}
